import { UiSuspensefulOutlet } from '@experiences/ui-common';
import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
    EditTenantMigration,
    TenantMigration,
    TenantSettings,
} from '../../../common/constants/RouteNames';
import { routePaths } from '../../../common/constants/routePaths';
import { useOrganizationName } from '../../../common/hooks/useOrganizationName';
import BreadcrumbProvider from '../../../common/providers/BreadcrumbProvider';
import { useTenantIdContext } from '../../../common/providers/TenantIdProvider';
import type { UiRouteObject } from '../../../container/routeConfigs/UiRouteObject';

const TenantSettingsPage = React.lazy(() => import('./TenantSettingsPageComponent'));
const TenantMigrationDetailsDrawer = React.lazy(() => import('../migrate/TenantMigrationDetailsDrawer').then(m => ({ default: m.TenantMigrationDetailsDrawer })));
const TenantMigrationPage = React.lazy(() => import('../migrate/TenantMigrationPage'));
const TenantMigrationEditSchedulePage = React.lazy(() => import('../migrate/TenantMigrationEditSchedulePage'));
const TenantMigrationPartialFailurePage = React.lazy(() => import('../migrate/TenantMigrationPartialFailurePage'));
const TenantMigrationCompletePage = React.lazy(() => import('../migrate/TenantMigrationCompletePage'));
const TenantMigrationProgressPage = React.lazy(() => import('../migrate/TenantMigrationProgressPage'));
const TenantMigrationRetryWizard = React.lazy(() => import('../migrate/TenantMigrationRetryWizard'));

const TenantSettingsProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { formatMessage: translate } = useIntl();

    const organizationName = useOrganizationName();
    const { tenantName } = useTenantIdContext();

    const breadcrumbs = useMemo(() => [
        {
            index: 0,
            link: routePaths.admin,
            name: organizationName,
        },
        {
            index: 1,
            link: routePaths.adminTenantHome,
            name: tenantName,
        },
        {
            index: 2,
            link: routePaths.adminTenantSettings,
            name: translate({ id: 'CLIENT_SETTINGS' }),
        },
    ], [ organizationName, tenantName, translate ]);

    return <BreadcrumbProvider breadcrumbs={breadcrumbs}>
        {children}
    </BreadcrumbProvider>;
};

export const TenantSettingsRoutes: UiRouteObject = {
    path: routePaths.adminTenantSettings,
    element: <TenantSettingsProvider>
        <UiSuspensefulOutlet />
    </TenantSettingsProvider>,
    children: [
        {
            path: routePaths.adminTenantSettings,
            redirectUrls: [ TenantSettings ],
            element: <>
                <TenantSettingsPage />
                <UiSuspensefulOutlet />
            </>,
            children: [
                {
                    path: routePaths.adminTenantSettingsMigrationDrawer,
                    element: <TenantMigrationDetailsDrawer />,
                    explicitEnvironments: [ 'cloud' ],
                },
            ],
        },
        {
            path: routePaths.adminTenantSettingsMigrationEdit,
            redirectUrls: [ EditTenantMigration ],
            element: <TenantMigrationEditSchedulePage />,
            explicitEnvironments: [ 'cloud' ],
        },
        {
            path: routePaths.adminTenantSettingsMigration,
            redirectUrls: [ TenantMigration ],
            element: <TenantMigrationPage />,
            explicitEnvironments: [ 'cloud' ],
        },
        {
            path: routePaths.adminTenantSettingsMigrationProgress,
            element: <TenantMigrationProgressPage />,
            explicitEnvironments: [ 'cloud' ],
        },
        {
            path: routePaths.adminTenantSettingsMigrationPartial,
            element: <TenantMigrationPartialFailurePage />,
            explicitEnvironments: [ 'cloud' ],
        },
        {
            path: routePaths.adminTenantSettingsMigrationComplete,
            element: <TenantMigrationCompletePage />,
            explicitEnvironments: [ 'cloud' ],
        },
        {
            path: routePaths.adminTenantSettingsMigrationRetry,
            element: <TenantMigrationRetryWizard />,
            explicitEnvironments: [ 'cloud' ],
        },
    ],
};
